/**
 * Flow Graph Types
 *
 * Types for the visual flow builder that defines the quickstart state machine.
 * Each node represents a phase, each edge represents a transition via execution pins.
 *
 * Key design decisions:
 * - Seamless vs standard loading phases are explicit (no runtime conditionals)
 * - 1:1 mapping: each QuickstartPhase maps to exactly one React component
 * - Phases can share the same component (e.g., all failure phases → QuickstartThankYouRedirect)
 */

// ============================================================================
// QUICKSTART PHASES
// ============================================================================

/**
 * Quickstart flow phases
 *
 * Standard phases use QuickstartLoading component.
 * Seamless phases use SeamlessLoadingOverlay component.
 * Specialized phases each map 1:1 to a specific React component.
 *
 * This is the authoritative list of all valid phases in the flow graph.
 * Each phase maps to exactly one React component (no runtime conditionals).
 */
export type QuickstartPhase =
  // Standard loading phases → QuickstartLoading
  | 'page_viewed'
  | 'environment_check'
  | 'creating_lobby'
  | 'matchmaking'
  | 'match_found'
  | 'joining_broadcast'

  // Fast-start matchmaking (replaces creating_lobby → matchmaking → match_found → joining_broadcast)
  | 'fast_start_matchmaking'

  // Seamless loading phases → SeamlessLoadingOverlay
  | 'seamless_page_viewed'
  | 'seamless_environment_check'
  | 'seamless_creating_lobby'
  | 'seamless_matchmaking'
  | 'seamless_match_found'
  | 'seamless_joining_broadcast'
  | 'seamless_fast_start_matchmaking'

  // Specialized phases (same component regardless of mode)
  | 'embedded_browser_blocked' // → EmbeddedBrowserRedirect
  | 'matchmaking_failed'       // → QuickstartThankYouRedirect
  | 'broadcast_join_failed'    // → QuickstartThankYouRedirect
  | 'playing_afk'              // → PlayerWithQuickstart
  | 'playing_active'           // → PlayerWithQuickstart
  | 'afk_timeout'              // → QuickstartThankYouRedirect
  | 'ad_timer_expired'         // → QuickstartThankYouRedirect
  | 'wishlist_early_exit'      // → QuickstartThankYouRedirect
  | 'cta_page'                 // → QuickstartCTA
  | 'auto_redirect'            // → QuickstartAutoRedirect
  | 'auth_attempted'           // → QuickstartAuth
  | 'auth_failed'              // → QuickstartAuth
  | 'thank_you'                // → QuickstartThankYouRedirect
  | 'error';                   // → QuickstartThankYouRedirect

// ============================================================================
// PHASE → COMPONENT MAPPING
// ============================================================================

/**
 * 1:1 mapping of phase → React component name.
 * Used by QuickStart.tsx to determine which component to render.
 */
export const PHASE_COMPONENT_MAP: Record<QuickstartPhase, string> = {
  // Standard loading phases
  page_viewed: 'QuickstartLoading',
  environment_check: 'QuickstartLoading',
  creating_lobby: 'QuickstartLoading',
  matchmaking: 'QuickstartLoading',
  match_found: 'QuickstartLoading',
  joining_broadcast: 'QuickstartLoading',
  fast_start_matchmaking: 'QuickstartLoading',

  // Seamless loading phases
  seamless_page_viewed: 'SeamlessLoadingOverlay',
  seamless_environment_check: 'SeamlessLoadingOverlay',
  seamless_creating_lobby: 'SeamlessLoadingOverlay',
  seamless_matchmaking: 'SeamlessLoadingOverlay',
  seamless_match_found: 'SeamlessLoadingOverlay',
  seamless_joining_broadcast: 'SeamlessLoadingOverlay',
  seamless_fast_start_matchmaking: 'SeamlessLoadingOverlay',

  // Specialized phases
  embedded_browser_blocked: 'EmbeddedBrowserRedirect',
  playing_afk: 'PlayerWithQuickstart',
  playing_active: 'PlayerWithQuickstart',
  cta_page: 'QuickstartCTA',
  auto_redirect: 'QuickstartAutoRedirect',
  auth_attempted: 'QuickstartAuth',
  auth_failed: 'QuickstartAuth',

  // Exit/failure phases
  matchmaking_failed: 'QuickstartThankYouRedirect',
  broadcast_join_failed: 'QuickstartThankYouRedirect',
  afk_timeout: 'QuickstartThankYouRedirect',
  ad_timer_expired: 'QuickstartThankYouRedirect',
  wishlist_early_exit: 'QuickstartThankYouRedirect',
  thank_you: 'QuickstartThankYouRedirect',
  error: 'QuickstartThankYouRedirect',
};

/** All valid phase values (for runtime validation) */
export const VALID_PHASES: QuickstartPhase[] = Object.keys(PHASE_COMPONENT_MAP) as QuickstartPhase[];

/** Human-readable display names for every phase */
export const PHASE_DISPLAY_NAMES: Record<QuickstartPhase, string> = {
  page_viewed: 'Page Viewed',
  environment_check: 'Environment Check',
  creating_lobby: 'Creating Lobby',
  matchmaking: 'Matchmaking',
  match_found: 'Match Found',
  joining_broadcast: 'Joining Broadcast',
  fast_start_matchmaking: 'Fast Start',

  seamless_page_viewed: 'Page Viewed (Seamless)',
  seamless_environment_check: 'Env Check (Seamless)',
  seamless_creating_lobby: 'Creating Lobby (Seamless)',
  seamless_matchmaking: 'Matchmaking (Seamless)',
  seamless_match_found: 'Match Found (Seamless)',
  seamless_joining_broadcast: 'Joining Broadcast (Seamless)',
  seamless_fast_start_matchmaking: 'Fast Start (Seamless)',

  embedded_browser_blocked: 'Embedded Browser Blocked',
  matchmaking_failed: 'Matchmaking Failed',
  broadcast_join_failed: 'Broadcast Join Failed',
  playing_afk: 'Playing (AFK)',
  playing_active: 'Playing (Active)',
  afk_timeout: 'AFK Timeout',
  ad_timer_expired: 'Ad Timer Expired',
  wishlist_early_exit: 'Wishlist Early Exit',
  cta_page: 'CTA Page',
  auto_redirect: 'Auto Redirect',
  auth_attempted: 'Auth Attempted',
  auth_failed: 'Auth Failed',
  thank_you: 'Thank You',
  error: 'Error',
};

// ============================================================================
// FLOW GRAPH DATA STRUCTURES
// ============================================================================

/**
 * A single node in the flow graph.
 * Each node represents one phase in the quickstart state machine
 * and maps 1:1 to a React component.
 */
export interface FlowNode {
  /** Unique identifier for this node instance */
  id: string;

  /** The phase this node represents */
  phase: QuickstartPhase;

  /** The React component to render (derived from PHASE_COMPONENT_MAP) */
  componentType: string;

  /** Node-specific configuration (timers, flags, etc.) */
  data: Record<string, unknown>;

  /** Position on the react-flow canvas */
  position: {
    x: number;
    y: number;
  };
}

/**
 * A directed edge connecting two nodes via execution pins.
 * Defines a transition from one phase to another based on a condition.
 */
export interface FlowEdge {
  /** Unique identifier for this edge */
  id: string;

  /** Source node ID */
  source: string;

  /** Source output pin name (e.g., 'success', 'failure', 'mobile', 'desktop') */
  sourceHandle: string;

  /** Target node ID */
  target: string;

  /** Target input pin name (default: 'input') */
  targetHandle?: string;
}

/**
 * Metadata about a flow graph.
 */
export interface FlowGraphMetadata {
  /** Schema version for future migrations */
  version: string;

  /** Unix timestamp (ms) when graph was created */
  createdAt: number;

  /** Unix timestamp (ms) when graph was last updated */
  updatedAt: number;

  /** User ID who created it (optional) */
  createdBy?: string;
}

/**
 * Complete flow graph for a campaign.
 * Defines the state machine that controls the player quickstart experience.
 */
export interface FlowGraph {
  /** All nodes in the graph */
  nodes: FlowNode[];

  /** All edges connecting nodes */
  edges: FlowEdge[];

  /** Graph metadata */
  metadata: FlowGraphMetadata;
}

// ============================================================================
// DEFAULT FLOW TEMPLATE
// ============================================================================

/**
 * Default flow graph template.
 * Matches the current hardcoded seamless quickstart behavior.
 * Used as fallback when a campaign has no custom flow graph.
 */
export const DEFAULT_FLOW_GRAPH: FlowGraph = {
  nodes: [
    { id: '1',  phase: 'seamless_page_viewed',          componentType: 'SeamlessLoadingOverlay',     data: {},                      position: { x: 0,    y: 200 } },
    { id: '2',  phase: 'seamless_environment_check',    componentType: 'SeamlessLoadingOverlay',     data: { blockEmbedded: true }, position: { x: 250,  y: 200 } },
    { id: '3',  phase: 'embedded_browser_blocked',      componentType: 'EmbeddedBrowserRedirect',    data: {},                      position: { x: 500,  y: 0 } },
    { id: '4',  phase: 'seamless_creating_lobby',       componentType: 'SeamlessLoadingOverlay',     data: {},                      position: { x: 500,  y: 200 } },
    { id: '5',  phase: 'seamless_matchmaking',          componentType: 'SeamlessLoadingOverlay',     data: { timeoutSeconds: 30 },  position: { x: 750,  y: 200 } },
    { id: '6',  phase: 'matchmaking_failed',            componentType: 'QuickstartThankYouRedirect', data: {},                      position: { x: 1000, y: 50 } },
    { id: '7',  phase: 'seamless_joining_broadcast',    componentType: 'SeamlessLoadingOverlay',     data: { timeoutSeconds: 15 },  position: { x: 1000, y: 200 } },
    { id: '8',  phase: 'broadcast_join_failed',         componentType: 'QuickstartThankYouRedirect', data: {},                      position: { x: 1250, y: 50 } },
    { id: '9',  phase: 'playing_afk',                   componentType: 'PlayerWithQuickstart',       data: {},                      position: { x: 1250, y: 200 } },
    { id: '10', phase: 'playing_active',                componentType: 'PlayerWithQuickstart',       data: {},                      position: { x: 1500, y: 200 } },
    { id: '11', phase: 'cta_page',                      componentType: 'QuickstartCTA',              data: {},                      position: { x: 1750, y: 200 } },
    { id: '12', phase: 'thank_you',                     componentType: 'QuickstartThankYouRedirect', data: {},                      position: { x: 2000, y: 200 } },
  ],
  edges: [
    // Entry
    { id: 'e1-2',    source: '1',  sourceHandle: 'next',          target: '2' },
    // Environment check branches
    { id: 'e2-3',    source: '2',  sourceHandle: 'embedded',      target: '3' },
    { id: 'e2-4a',   source: '2',  sourceHandle: 'desktop',       target: '4' },
    { id: 'e2-4b',   source: '2',  sourceHandle: 'mobile',        target: '4' },
    // Matchmaking
    { id: 'e4-5',    source: '4',  sourceHandle: 'success',       target: '5' },
    { id: 'e5-6',    source: '5',  sourceHandle: 'failure',       target: '6' },
    { id: 'e5-7',    source: '5',  sourceHandle: 'success',       target: '7' },
    // Connection
    { id: 'e7-8',    source: '7',  sourceHandle: 'failure',       target: '8' },
    { id: 'e7-9',    source: '7',  sourceHandle: 'success',       target: '9' },
    // Play
    { id: 'e9-10',   source: '9',  sourceHandle: 'play',          target: '10' },
    { id: 'e10-11a', source: '10', sourceHandle: 'afkTimeout',    target: '11' },
    { id: 'e10-11b', source: '10', sourceHandle: 'adExpired',     target: '11' },
    { id: 'e10-11c', source: '10', sourceHandle: 'wishlistExit',  target: '11' },
    // Exit
    { id: 'e11-12',  source: '11', sourceHandle: 'complete',      target: '12' },
  ],
  metadata: {
    version: '1.0',
    createdAt: 0,
    updatedAt: 0,
  },
};
